import microsoftLogo from "../assets/images/microsoft.svg";
import dropboxLogo from "../assets/images/dropbox.svg";
import shopifyLogo from "../assets/images/shopify.svg";
import zoomLogo from "../assets/images/zoom.svg";
import calendlyLogo from "../assets/images/calendly.svg";
import slackLogo from "../assets/images/slack.svg";
import notionLogo from "../assets/images/notion.svg";
import bitlyLogo from "../assets/images/bitly.svg";
import discordLogo from "../assets/images/discord.svg";
import yahooLogo from "../assets/images/yahoo.svg";

export const OAUTH_PROVIDERS = {
    microsoft: {
        id: "microsoft",
        name: "Microsoft App",
        buttonText: "Sign in with Microsoft",
        logo: microsoftLogo,
        defaultScopes: ["User.Read"],
        requiredScopes: ["User.Read"],
        scopeDelimiter: " ",
        scopeGroups: {
            "User Profile": [
                { id: "User.Read", scope: "User.Read", label: "Read Profile", description: "Sign in and read your basic profile" },
                { id: "User.ReadBasic.All", scope: "User.ReadBasic.All", label: "Read Organization Users", description: "View basic profiles of all users in your organization" },
            ],
            "Outlook & Mail": [
                { id: "Mail.Read", scope: "Mail.Read", label: "Read Mail", description: "View your email messages and settings" },
                { id: "Mail.ReadWrite", scope: "Mail.ReadWrite", label: "Read & Write Mail", description: "Create, read, update, and delete your email" },
                { id: "Mail.Send", scope: "Mail.Send", label: "Send Mail", description: "Send email messages on your behalf" },
                { id: "MailboxSettings.Read", scope: "MailboxSettings.Read", label: "Mailbox Settings", description: "Read your mailbox settings" },
            ],
            "OneDrive & Files": [
                { id: "Files.Read", scope: "Files.Read", label: "Read Files", description: "Read all your OneDrive and SharePoint files" },
                { id: "Files.ReadWrite", scope: "Files.ReadWrite", label: "Read & Write Files", description: "Create, read, update, and delete all your files" },
                { id: "Files.Read.All", scope: "Files.Read.All", label: "Read All Shared Files", description: "Read all files you have access to across your organization" },
                { id: "Files.ReadWrite.All", scope: "Files.ReadWrite.All", label: "Full Files Access", description: "Create, edit, and delete all files you have access to" },
            ],
            "Calendars": [
                { id: "Calendars.Read", scope: "Calendars.Read", label: "Read Calendars", description: "View events in your calendars" },
                { id: "Calendars.ReadWrite", scope: "Calendars.ReadWrite", label: "Read & Write Calendars", description: "Create, read, update, and delete events in your calendars" },
            ],
            "Contacts": [
                { id: "Contacts.Read", scope: "Contacts.Read", label: "Read Contacts", description: "View your contacts" },
                { id: "Contacts.ReadWrite", scope: "Contacts.ReadWrite", label: "Read & Write Contacts", description: "Create, read, update, and delete your contacts" },
            ],
            "Tasks & To-Do": [
                { id: "Tasks.Read", scope: "Tasks.Read", label: "Read Tasks", description: "View your tasks and to-do lists" },
                { id: "Tasks.ReadWrite", scope: "Tasks.ReadWrite", label: "Read & Write Tasks", description: "Create, read, update, and delete your tasks and to-do items" },
            ],
            "Teams & Chat": [
                { id: "Chat.Read", scope: "Chat.Read", label: "Read Chats", description: "Read your 1-on-1 and group chat messages" },
                { id: "ChatMessage.Send", scope: "ChatMessage.Send", label: "Send Messages", description: "Send messages in chats on your behalf" },
            ],
            "OneNote & Notes": [
                { id: "Notes.Read", scope: "Notes.Read", label: "Read Notes", description: "Read your OneNote notebooks and pages" },
                { id: "Notes.ReadWrite", scope: "Notes.ReadWrite", label: "Read & Write Notes", description: "Create and edit your OneNote notebooks" },
            ],
        },
    },

    dropbox: {
        id: "dropbox",
        name: "Dropbox",
        buttonText: "Sign in with Dropbox",
        logo: dropboxLogo,
        defaultScopes: ["account_info.read", "files.content.read", "files.content.write"],
        requiredScopes: ["account_info.read"],
        scopeDelimiter: " ",
        scopeGroups: {
            "Account & Profile": [
                { id: "account_info.read", scope: "account_info.read", label: "Account Info", description: "View basic information about your Dropbox account" },
            ],
            "Files & Folders": [
                { id: "files.content.read", scope: "files.content.read", label: "Read File Content", description: "View and download your files and folders" },
                { id: "files.content.write", scope: "files.content.write", label: "Write File Content", description: "Create, edit, and delete files in your Dropbox" },
                { id: "files.metadata.read", scope: "files.metadata.read", label: "Read Metadata", description: "View metadata for your files and folders" },
                { id: "files.metadata.write", scope: "files.metadata.write", label: "Write Metadata", description: "Edit metadata for your files and folders" },
            ],
            "Sharing": [
                { id: "sharing.read", scope: "sharing.read", label: "Read Sharing", description: "View sharing settings and shared links" },
                { id: "sharing.write", scope: "sharing.write", label: "Manage Sharing", description: "Create, modify, and delete shared links and folders" },
            ],
            "File Requests": [
                { id: "file_requests.read", scope: "file_requests.read", label: "Read File Requests", description: "View your file requests" },
                { id: "file_requests.write", scope: "file_requests.write", label: "Manage File Requests", description: "Create and edit file requests" },
            ],
            "Contacts": [
                { id: "contacts.read", scope: "contacts.read", label: "Read Contacts", description: "View your Dropbox contacts" },
                { id: "contacts.write", scope: "contacts.write", label: "Manage Contacts", description: "Edit your Dropbox contacts" },
            ],
        },
    },

    shopify: {
        id: "shopify",
        name: "Shopify",
        buttonText: "Sign in with Shopify",
        logo: shopifyLogo,
        requiresShopDomain: true,
        shopDomainPlaceholder: "your-store.myshopify.com",
        defaultScopes: ["read_products", "read_orders", "read_customers"],
        requiredScopes: ["read_products"],
        scopeDelimiter: ",",
        scopeGroups: {
            "Products & Catalog": [
                { id: "read_products", scope: "read_products", label: "Read Products", description: "View products, variants, collections, and images" },
                { id: "write_products", scope: "write_products", label: "Manage Products", description: "Create, update, and delete products and collections" },
                { id: "read_product_listings", scope: "read_product_listings", label: "Read Product Listings", description: "View product listings on sales channels" },
            ],
            "Orders & Transactions": [
                { id: "read_orders", scope: "read_orders", label: "Read Orders", description: "View orders, transactions, and fulfillments" },
                { id: "write_orders", scope: "write_orders", label: "Manage Orders", description: "Create, update, and cancel orders" },
                { id: "read_all_orders", scope: "read_all_orders", label: "Read All Orders History", description: "View complete order history older than 60 days" },
                { id: "read_draft_orders", scope: "read_draft_orders", label: "Read Draft Orders", description: "View draft orders and quotes" },
                { id: "write_draft_orders", scope: "write_draft_orders", label: "Manage Draft Orders", description: "Create and modify draft orders" },
            ],
            "Customers": [
                { id: "read_customers", scope: "read_customers", label: "Read Customers", description: "View customer profiles and order histories" },
                { id: "write_customers", scope: "write_customers", label: "Manage Customers", description: "Create, update, and delete customer profiles" },
            ],
            "Inventory & Locations": [
                { id: "read_inventory", scope: "read_inventory", label: "Read Inventory", description: "View inventory levels and tracking details" },
                { id: "write_inventory", scope: "write_inventory", label: "Manage Inventory", description: "Update stock levels and inventory locations" },
                { id: "read_locations", scope: "read_locations", label: "Read Locations", description: "View business and warehouse locations" },
            ],
            "Fulfillment & Shipping": [
                { id: "read_fulfillments", scope: "read_fulfillments", label: "Read Fulfillments", description: "View order fulfillments and tracking info" },
                { id: "write_fulfillments", scope: "write_fulfillments", label: "Manage Fulfillments", description: "Create and update fulfillments" },
                { id: "read_shipping", scope: "read_shipping", label: "Read Shipping Rates", description: "View shipping rates and delivery zones" },
                { id: "write_shipping", scope: "write_shipping", label: "Manage Shipping", description: "Update shipping and carrier services" },
            ],
            "Analytics & Reports": [
                { id: "read_analytics", scope: "read_analytics", label: "Read Analytics", description: "View store analytics and performance metrics" },
                { id: "read_reports", scope: "read_reports", label: "Read Reports", description: "View sales and business reports" },
                { id: "write_reports", scope: "write_reports", label: "Manage Reports", description: "Create and modify custom reports" },
            ],
            "Content & Themes": [
                { id: "read_themes", scope: "read_themes", label: "Read Themes", description: "View store themes and assets" },
                { id: "write_themes", scope: "write_themes", label: "Manage Themes", description: "Modify store themes and templates" },
                { id: "read_content", scope: "read_content", label: "Read Store Content", description: "View blogs, articles, and pages" },
                { id: "write_content", scope: "write_content", label: "Manage Store Content", description: "Create and edit blog posts and pages" },
            ],
        },
    },

    zoom: {
        id: "zoom",
        name: "Zoom",
        buttonText: "Sign in with Zoom",
        logo: zoomLogo,
        defaultScopes: ["meeting:read:meeting", "meeting:write:meeting", "user:read:user"],
        requiredScopes: [],
        scopeDelimiter: " ",
        scopeGroups: {
            "Meetings": [
                { id: "meeting:read:meeting", scope: "meeting:read:meeting", label: "View Meetings", description: "View your meetings, participants, and settings" },
                { id: "meeting:write:meeting", scope: "meeting:write:meeting", label: "Manage Meetings", description: "Create, update, and delete meetings on your behalf" },
            ],
            "Cloud Recordings": [
                { id: "recording:read:recording", scope: "recording:read:recording", label: "Read Recordings", description: "View and download cloud meeting recordings and transcripts" },
                { id: "recording:write:recording", scope: "recording:write:recording", label: "Manage Recordings", description: "Delete and manage cloud recordings" },
            ],
            "Webinars": [
                { id: "webinar:read:webinar", scope: "webinar:read:webinar", label: "View Webinars", description: "View webinar schedules, details, and attendee lists" },
                { id: "webinar:write:webinar", scope: "webinar:write:webinar", label: "Manage Webinars", description: "Create and update webinars and panelist invites" },
            ],
            "User & Profile": [
                { id: "user:read:user", scope: "user:read:user", label: "Read Profile", description: "View your Zoom user profile and account details" },
                { id: "user:write:user", scope: "user:write:user", label: "Manage Settings", description: "Update user profile and account settings" },
            ],
        },
    },

    calendly: {
        id: "calendly",
        name: "Calendly",
        buttonText: "Sign in with Calendly",
        logo: calendlyLogo,
        defaultScopes: ["default"],
        requiredScopes: ["default"],
        scopeDelimiter: " ",
        scopeGroups: {
            "Scheduled Events & Bookings": [
                { id: "default:events:read", scope: "default", label: "Read Events & Bookings", description: "View scheduled meetings, bookings, and attendee details" },
                { id: "default:events:write", scope: "default", label: "Manage Bookings", description: "Cancel or reschedule meetings and bookings" },
            ],
            "Event Types & Links": [
                { id: "default:event_types:read", scope: "default", label: "Read Event Types", description: "View booking links, durations, and meeting types" },
            ],
            "User & Organization Profile": [
                { id: "default:user:read", scope: "default", label: "Read User Profile", description: "View user account details and timezone settings" },
                { id: "default:organization:read", scope: "default", label: "Read Organization Info", description: "View organization memberships and team settings" },
            ],
            "Webhooks & Subscriptions": [
                { id: "default:webhook_subscriptions:read", scope: "default", label: "Read Webhooks", description: "View active webhook subscriptions" },
                { id: "default:webhook_subscriptions:write", scope: "default", label: "Manage Webhooks", description: "Create and delete real-time webhook subscriptions" },
            ],
        },
    },

    slack: {
        id: "slack",
        name: "Slack",
        buttonText: "Sign in with Slack",
        logo: slackLogo,
        defaultScopes: ["channels:read", "chat:write", "users:read"],
        requiredScopes: [],
        scopeDelimiter: ",",
        scopeGroups: {
            "Channels & Conversations": [
                { id: "channels:read", scope: "channels:read", label: "View Public Channels", description: "View basic information about public channels in a workspace" },
                { id: "channels:history", scope: "channels:history", label: "Read Public Channel Messages", description: "View messages and other content in public channels" },
                { id: "channels:join", scope: "channels:join", label: "Join Public Channels", description: "Join public channels on workspace" },
                { id: "groups:read", scope: "groups:read", label: "View Private Channels", description: "View basic information about private channels the app is added to" },
                { id: "groups:history", scope: "groups:history", label: "Read Private Channel Messages", description: "View messages in private channels the app is in" },
            ],
            "Messages & Chat": [
                { id: "chat:write", scope: "chat:write", label: "Send Messages", description: "Send messages as the app" },
                { id: "chat:write.public", scope: "chat:write.public", label: "Send Messages to Any Public Channel", description: "Send messages to public channels without joining them" },
                { id: "chat:write.customize", scope: "chat:write.customize", label: "Customize Messages", description: "Send messages with customized username and avatar" },
            ],
            "Users & Workspace Profile": [
                { id: "users:read", scope: "users:read", label: "View Workspace Users", description: "View people in the workspace" },
                { id: "users:read.email", scope: "users:read.email", label: "View User Emails", description: "View email addresses of people in the workspace" },
                { id: "users.profile:read", scope: "users.profile:read", label: "View User Profiles", description: "View profile details of workspace members" },
                { id: "team:read", scope: "team:read", label: "View Workspace Info", description: "View name, email domain, and icon of the workspace" },
            ],
            "Files & Sharing": [
                { id: "files:read", scope: "files:read", label: "View Files", description: "View files shared in channels and conversations" },
                { id: "files:write", scope: "files:write", label: "Upload Files", description: "Upload, edit, and delete files" },
            ],
            "Reactions & Emojis": [
                { id: "reactions:read", scope: "reactions:read", label: "View Emoji Reactions", description: "View emoji reactions and messages they are attached to" },
                { id: "reactions:write", scope: "reactions:write", label: "Add Emoji Reactions", description: "Add and remove emoji reactions on messages" },
            ],
        },
    },

    notion: {
        id: "notion",
        name: "Notion",
        buttonText: "Sign in with Notion",
        logo: notionLogo,
        defaultScopes: [],
        requiredScopes: [],
        scopeDelimiter: " ",
        scopeGroups: {
            "Content & Pages": [
                { id: "notion:read_content", scope: "read_content", label: "Read Content & Pages", description: "Read page content, properties, and database entries" },
                { id: "notion:update_content", scope: "update_content", label: "Update Content", description: "Update existing pages, properties, and database records" },
                { id: "notion:insert_content", scope: "insert_content", label: "Insert & Create Content", description: "Create new pages, databases, and blocks" },
            ],
            "User & Workspace": [
                { id: "notion:read_user_info", scope: "read_user_info", label: "Read User Info", description: "View workspace member names and avatar profiles" },
                { id: "notion:read_user_email", scope: "read_user_email", label: "Read User Email", description: "View email addresses of workspace members" },
            ],
            "Comments": [
                { id: "notion:read_comments", scope: "read_comments", label: "Read Comments", description: "View discussion comments on pages and blocks" },
                { id: "notion:insert_comments", scope: "insert_comments", label: "Create Comments", description: "Add comments and replies to pages" },
            ],
        },
    },

    bitly: {
        id: "bitly",
        name: "Bitly",
        buttonText: "Sign in with Bitly",
        logo: bitlyLogo,
        defaultScopes: [],
        requiredScopes: [],
        scopeDelimiter: " ",
        scopeGroups: {
            "Links & Shortening": [
                { id: "bitly:shorten_links", scope: "shorten_links", label: "Create & Shorten Links", description: "Shorten URLs and customize backhalves" },
                { id: "bitly:manage_links", scope: "manage_links", label: "Manage Bitlinks", description: "Edit title, tags, redirects, and archived status" },
            ],
            "Analytics & Metrics": [
                { id: "bitly:view_metrics", scope: "view_metrics", label: "View Click Analytics", description: "Track click counts, referring channels, and country breakdown" },
            ],
            "Groups & Organizations": [
                { id: "bitly:manage_groups", scope: "manage_groups", label: "Group Management", description: "Access organization groups, domains, and custom settings" },
            ],
        },
    },

    discord: {
        id: "discord",
        name: "Discord",
        buttonText: "Sign in with Discord",
        logo: discordLogo,
        defaultScopes: ["identify", "email", "guilds"],
        requiredScopes: ["identify"],
        scopeDelimiter: " ",
        scopeGroups: {
            "Identity & User Profile": [
                { id: "discord:identify", scope: "identify", label: "Identify", description: "Access your username, avatar, and discriminator without password" },
                { id: "discord:email", scope: "email", label: "Email Address", description: "Access your verified email address" },
                { id: "discord:connections", scope: "connections", label: "Linked Connections", description: "View your linked third-party accounts (Twitch, YouTube, Steam, etc.)" },
            ],
            "Guilds & Servers": [
                { id: "discord:guilds", scope: "guilds", label: "View Servers", description: "Know which servers and communities you belong to" },
                { id: "discord:guilds.members.read", scope: "guilds.members.read", label: "Server Member Info", description: "Read member information (roles, nicknames) in servers you belong to" },
                { id: "discord:guilds.join", scope: "guilds.join", label: "Join Servers", description: "Allows joining you to a server" },
            ],
            "Messages & Channels": [
                { id: "discord:messages.read", scope: "messages.read", label: "Read Messages", description: "Read channel messages and content" },
                { id: "discord:webhook.incoming", scope: "webhook.incoming", label: "Incoming Webhooks", description: "Create and post incoming webhooks to channels" },
                { id: "discord:role_connections.write", scope: "role_connections.write", label: "Update Linked Roles", description: "Update your role metadata for verified servers" },
            ],
        },
    },

    yahoo: {
        id: "yahoo",
        name: "Yahoo",
        buttonText: "Sign in with Yahoo",
        logo: yahooLogo,
        defaultScopes: ["openid", "profile", "email"],
        requiredScopes: ["openid"],
        scopeDelimiter: " ",
        scopeGroups: {
            "Profile & Identity": [
                { id: "yahoo:openid", scope: "openid", label: "OpenID", description: "Authenticate using your Yahoo account" },
                { id: "yahoo:profile", scope: "profile", label: "User Profile", description: "Access your basic profile information (nickname, name, gender)" },
                { id: "yahoo:email", scope: "email", label: "Email Address", description: "View your verified Yahoo email address" },
            ],
            "Fantasy Sports": [
                { id: "yahoo:fspt-r", scope: "fspt-r", label: "Read Fantasy Sports", description: "Read your Fantasy Sports teams and leagues" },
            ],
            "Yahoo Mail": [
                { id: "yahoo:mail-r", scope: "mail-r", label: "Read Mail (Requires App Permission)", description: "Read email messages and folders in Yahoo Mail" },
                { id: "yahoo:mail-w", scope: "mail-w", label: "Send & Manage Mail (Requires App Permission)", description: "Compose, send, and manage Yahoo mail messages" },
            ],
            "Contacts & Address Book": [
                { id: "yahoo:sdct-r", scope: "sdct-r", label: "Read Contacts (Requires App Permission)", description: "Read address book contacts and connections" },
                { id: "yahoo:sdct-w", scope: "sdct-w", label: "Manage Contacts (Requires App Permission)", description: "Add and update address book contacts" },
            ],
        },
    },
};

export const resolveOAuthProvider = (keyOrType) => {
    if (!keyOrType) return null;
    const lower = keyOrType.toLowerCase().replace(/[\s-_]/g, "");

    if (lower.includes("microsoft") || lower.includes("onedrive") || lower.includes("office365")) {
        return OAUTH_PROVIDERS.microsoft;
    }
    if (lower.includes("dropbox")) {
        return OAUTH_PROVIDERS.dropbox;
    }
    if (lower.includes("shopify")) {
        return OAUTH_PROVIDERS.shopify;
    }
    if (lower.includes("zoom")) {
        return OAUTH_PROVIDERS.zoom;
    }
    if (lower.includes("calendly")) {
        return OAUTH_PROVIDERS.calendly;
    }
    if (lower.includes("slack")) {
        return OAUTH_PROVIDERS.slack;
    }
    if (lower.includes("notion")) {
        return OAUTH_PROVIDERS.notion;
    }
    if (lower.includes("bitly")) {
        return OAUTH_PROVIDERS.bitly;
    }
    if (lower.includes("discord")) {
        return OAUTH_PROVIDERS.discord;
    }
    if (lower.includes("yahoo")) {
        return OAUTH_PROVIDERS.yahoo;
    }

    return OAUTH_PROVIDERS[lower] || null;
};


